import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { Landmark, Percent, Clock, ShieldCheck, Calculator } from "lucide-react";
import { fadeUp, stagger } from "@/lib/motion";

// Indicative partner list for Ethiopian mortgage financing.
const BANKS = [
  { name: "Commercial Bank of Ethiopia", short: "CBE", color: "#7A1F2B" },
  { name: "Awash Bank", short: "AW", color: "#0E7C4A" },
  { name: "Dashen Bank", short: "DB", color: "#1E4FA3" },
  { name: "Bank of Abyssinia", short: "BoA", color: "#C9A227" },
  { name: "Cooperative Bank of Oromia", short: "CBO", color: "#1D8A4C" },
  { name: "Wegagen Bank", short: "WG", color: "#1E3A8A" },
  { name: "Hibret Bank", short: "HB", color: "#8A1E5A" },
  { name: "Zemen Bank", short: "ZB", color: "#0E6E8A" },
];

const PROGRAMS = [
  { icon: Percent, title: "Home Purchase Mortgage", desc: "Up to 85% financing on verified residential properties.", meta: "From ~9.5% p.a. (indicative)" },
  { icon: Clock, title: "Long-Tenure Repayment", desc: "Flexible repayment plans stretching up to 20 years.", meta: "Up to 20 years" },
  { icon: ShieldCheck, title: "Diaspora Mortgage", desc: "Dedicated financing programs for Ethiopians abroad.", meta: "FCY & local options" },
];

export const PartnerBanks = () => {
  const navigate = useNavigate();

  return (
    <section className="bg-muted/40 py-12 sm:py-16" data-testid="partner-banks-section">
      <div className="abay-container">
      <div className="max-w-2xl">
        <span className="inline-flex items-center gap-2 rounded-full bg-[#1E3A8A]/8 px-3 py-1 text-xs font-semibold text-[#1E3A8A] dark:bg-[#7AA0FF]/12 dark:text-[#7AA0FF]">
          <Landmark className="h-3.5 w-3.5" /> Financing made simple
        </span>
        <h2 className="mt-3 text-3xl font-bold tracking-tight text-foreground sm:text-4xl">Partner Banks &amp; Mortgage Programs</h2>
        <p className="mt-3 text-muted-foreground">Finance your next property through Ethiopia&apos;s leading banks. Compare programs and estimate repayments right on ABAY360.</p>
      </div>

      {/* Bank logo grid */}
      <motion.div
        variants={stagger}
        initial="hidden"
        whileInView="show"
        viewport={{ once: true, margin: "-80px" }}
        className="mt-8 grid grid-cols-2 gap-3 sm:grid-cols-4 sm:gap-4"
      >
        {BANKS.map((b) => (
          <motion.div
            key={b.short}
            variants={fadeUp}
            data-testid={`partner-bank-${b.short.toLowerCase()}`}
            className="flex items-center gap-3 rounded-2xl border border-border bg-card p-4 shadow-[var(--shadow-sm)] transition-shadow duration-200 hover:shadow-[var(--shadow-md)]"
          >
            <span
              className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl text-sm font-extrabold text-white"
              style={{ backgroundColor: b.color }}
              aria-hidden
            >
              {b.short}
            </span>
            <span className="min-w-0 text-sm font-semibold leading-tight text-foreground">{b.name}</span>
          </motion.div>
        ))}
      </motion.div>

      {/* Mortgage programs */}
      <div className="mt-8 grid grid-cols-1 gap-5 lg:grid-cols-3">
        {PROGRAMS.map((p) => (
          <div
            key={p.title}
            data-testid={`mortgage-program-${p.title.toLowerCase().replace(/[^a-z]+/g, "-").replace(/^-|-$/g, "")}`}
            className="rounded-2xl border border-border bg-card p-6 shadow-[var(--shadow-sm)]"
          >
            <span className="flex h-11 w-11 items-center justify-center rounded-2xl bg-[#10B981]/12 text-[#0E9F6E]">
              <p.icon className="h-5 w-5" />
            </span>
            <h3 className="mt-4 text-lg font-semibold text-foreground">{p.title}</h3>
            <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{p.desc}</p>
            <p className="mt-4 text-sm font-semibold text-[#1E3A8A] dark:text-[#7AA0FF]">{p.meta}</p>
          </div>
        ))}
      </div>

      <div className="mt-8 flex flex-wrap items-center gap-4">
        <button
          type="button"
          data-testid="partner-banks-cta"
          onClick={() => navigate("/mortgage")}
          className="tap-press inline-flex items-center gap-2 rounded-xl bg-[#1E3A8A] px-6 py-3 text-sm font-semibold text-white shadow-[0_10px_24px_rgba(30,58,138,0.24)] transition-[filter,box-shadow] duration-200 hover:brightness-[1.05]"
        >
          <Calculator className="h-4 w-4" /> Mortgage Dashboard
        </button>
        <p className="text-xs text-muted-foreground">Rates and programs are indicative and subject to each bank&apos;s approval.</p>
      </div>
      </div>
    </section>
  );
};
